import type { SessionPort } from "../../domain/ports/SessionPort";
import type { User } from "../../domain/entities/User";
import { notifySessionTokensChanged } from "../../../../core/api/sessionEvents";
import {
  readSharedAuthItem,
  removeSharedAuthItem,
  writeSharedAuthItem,
} from "../../../../core/api/sharedAuthStorage";

const REGION_KEY = "region_key";
const CLINICAL_API_BASE_URL_KEY = "clinical_api_base_url";

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, "");

export class SessionManager implements SessionPort {
  // Persisted in localStorage (not sessionStorage) so it survives exactly as
  // long as the access/refresh tokens it's paired with — a fresh tab still
  // has the tokens from localStorage, so it must still have the regional API
  // URL those tokens depend on, or every non-auth-host request (patients,
  // appointments, …) silently fails to resolve a base URL.
  saveRegionalApi(region?: { region_key?: string; api_base_url?: string }): void {
    if (region?.region_key) {
      localStorage.setItem(REGION_KEY, region.region_key);
    }
    if (region?.api_base_url) {
      localStorage.setItem(
        CLINICAL_API_BASE_URL_KEY,
        stripTrailingSlash(region.api_base_url),
      );
    }
  }

  saveSession(user: User): void {
    // Never overwrite a valid token with an empty/falsy value
    if (user.access_token) writeSharedAuthItem("access_token", user.access_token);
    if (user.refresh_token) writeSharedAuthItem("refresh_token", user.refresh_token);
    sessionStorage.setItem("temp_access_token", user.temp_access_token || "");
    sessionStorage.setItem("is_totp_enabled", String(user.is_totp_enabled));
    sessionStorage.setItem("is_password_set", String(user.is_password_set));
    sessionStorage.setItem("user_email", user.email);
    this.saveRegionalApi(user);
    notifySessionTokensChanged();
  }

  savePendingMfaSession(user: User): void {
    removeSharedAuthItem("access_token");
    removeSharedAuthItem("refresh_token");

    if (user.temp_access_token) {
      sessionStorage.setItem("temp_access_token", user.temp_access_token);
    } else {
      sessionStorage.removeItem("temp_access_token");
    }

    sessionStorage.setItem("is_totp_enabled", String(user.is_totp_enabled));
    sessionStorage.setItem("is_password_set", String(user.is_password_set));
    sessionStorage.setItem("user_email", user.email);
    this.saveRegionalApi(user);
    notifySessionTokensChanged();
  }

  saveTempAccessToken(temp_access_token: string): void {
    sessionStorage.setItem("temp_access_token", temp_access_token);
  }

  saveTokens(tokens: {
    access_token: string;
    refresh_token: string;
    temp_access_token?: string;
  }): void {
    const tempToken = tokens.temp_access_token || tokens.access_token || "";

    if (tokens.access_token) {
      writeSharedAuthItem("access_token", tokens.access_token);
    }
    if (tokens.refresh_token) {
      writeSharedAuthItem("refresh_token", tokens.refresh_token);
    }
    if (tempToken) {
      sessionStorage.setItem("temp_access_token", tempToken);
    }
    notifySessionTokensChanged();
  }

  getAccessToken(): string | null {
    return readSharedAuthItem("access_token");
  }

  getRefreshToken(): string | null {
    return readSharedAuthItem("refresh_token");
  }

  getTempAccessToken(): string | null {
    return sessionStorage.getItem("temp_access_token");
  }

  getPasswordResetToken(): string | null {
    return this.getTempAccessToken() || this.getAccessToken();
  }

  getClinicalApiBaseUrl(): string | null {
    return this.readMigratedRegionalItem(CLINICAL_API_BASE_URL_KEY);
  }

  getRegionKey(): string | null {
    return this.readMigratedRegionalItem(REGION_KEY);
  }

  // One-time migration for tabs still holding a pre-fix sessionStorage value.
  private readMigratedRegionalItem(key: string): string | null {
    const current = localStorage.getItem(key);
    if (current !== null) return current;

    const legacy = sessionStorage.getItem(key);
    if (legacy !== null) {
      localStorage.setItem(key, legacy);
      sessionStorage.removeItem(key);
      return legacy;
    }

    return null;
  }

  getIsTotpEnabled(): boolean | null {
    const value =
      sessionStorage.getItem("is_totp_enabled") ??
      sessionStorage.getItem("totp_enabled");
    return value !== null ? value === "true" : null;
  }

  getTotpEnabled(): boolean | null {
    return this.getIsTotpEnabled();
  }

  getIsPasswordSet(): boolean | null {
    const value = sessionStorage.getItem("is_password_set");
    return value !== null ? value === "true" : null;
  }

  getUserEmail(): string | null {
    return sessionStorage.getItem("user_email");
  }

  clearSession(): void {
    removeSharedAuthItem("access_token");
    removeSharedAuthItem("refresh_token");
    sessionStorage.removeItem("temp_access_token");
    sessionStorage.removeItem("is_totp_enabled");
    sessionStorage.removeItem("totp_enabled");
    sessionStorage.removeItem("is_password_set");
    sessionStorage.removeItem("user_email");
    localStorage.removeItem(REGION_KEY);
    localStorage.removeItem(CLINICAL_API_BASE_URL_KEY);
    // Clean up stale copies from before this moved to localStorage.
    sessionStorage.removeItem(REGION_KEY);
    sessionStorage.removeItem(CLINICAL_API_BASE_URL_KEY);
    notifySessionTokensChanged();
  }
}
